import type { ComponentPropsWithoutRef, Ref } from 'react';
import { Skeleton } from '@monksflow/monks-ui';

import { SmartStartCardV4 } from '../SmartStartCard/SmartStartCard.v4';
import type { SmartStartGridProps } from './SmartStartGrid.types';
import {
  smartStartGridV4Variants,
  type SmartStartGridV4VariantProps,
} from './SmartStartGrid.v4.variants';
import { useTranslation } from 'react-i18next';

export interface SmartStartGridV4Props
  extends
    Omit<ComponentPropsWithoutRef<'div'>, keyof SmartStartGridProps>,
    SmartStartGridProps,
    SmartStartGridV4VariantProps {
  /** React 19 ref-as-prop: forwarded to the underlying `<div>`. */
  ref?: Ref<HTMLDivElement>;
}

export function SmartStartGridV4({
  tabId,
  smartCardsResponse,
  onCardClick,
  className,
  ref,
  ...rest
}: SmartStartGridV4Props) {
  const { t } = useTranslation('springboard');

  const { cards, isLoading, isError } = smartCardsResponse;

  const rootProps = {
    ref,
    'data-slot': 'smart-start-grid',
    className: smartStartGridV4Variants({ className }),
    ...rest,
  };

  if (isLoading) {
    return (
      <div {...rootProps}>
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={`smartstart-skeleton-${index}`}
            className="flex items-center gap-4 p-4 rounded-2xl bg-card border border-border-light"
          >
            <Skeleton
              className="size-10 rounded-lg shrink-0"
              data-testid="smartstart-skeleton-loader"
            />
            <Skeleton
              className="w-[180px] h-5 rounded-sm"
              data-testid="smartstart-skeleton-loader"
            />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div {...rootProps} className={[rootProps.className, 'py-4'].join(' ')}>
        <p className="text-base text-destructive">
          {t('springboard.smartStartGridV4.failedToLoadSmartStart')}
        </p>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div {...rootProps} className={[rootProps.className, 'py-4'].join(' ')}>
        <p className="text-base text-muted-foreground">
          {t('springboard.smartStartGridV4.noSmartStartCardsAvailable')}
        </p>
      </div>
    );
  }

  return (
    <div {...rootProps}>
      {cards.map((item) => (
        <SmartStartCardV4
          key={item.id}
          title={item.title}
          agentId={item.input.agentId}
          tabId={tabId}
          disabled={item.disabled}
          onClick={() => {
            if (item.disabled) return;
            onCardClick(item);
          }}
        />
      ))}
    </div>
  );
}

SmartStartGridV4.displayName = 'SmartStartGridV4';
